"use client";

import { useLocale } from "@i18n/react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import type { AvatarSchema } from "../../../lib/types/schema/user/avatarSchema";
import type { Me } from "../../../lib/types/schema/user/meSchema";
import { useRequestMutation } from "../../../request/hooks/useRequestMutation";
import { useRequestQuery } from "../../../request/hooks/useRequestQuery";
import { keyAvatars } from "../avatars/keyAvatars";
import { keyMe } from "./keyMe";
import { requestMeProps } from "./requestMe";

export const useRequestMe = () => {
  const locale = useLocale();
  const queryClient = useQueryClient();

  return {
    mutationAvatar: useRequestMutation({
      mutationFnRequestProps: (data: Pick<AvatarSchema, "id">) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/me/avatar",
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [keyAvatars(), locale] });
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] });
      },
    }),
    mutationDelete: useRequestMutation({
      mutationFnRequestProps: {
        method: "DELETE",
        schema: z.boolean(),
        urlBackendPath: "/me",
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] }),
    }),
    mutationEmail: useRequestMutation({
      mutationFnRequestProps: (data: Pick<Me, "email">) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/me/email",
      }),
      meta: { suppressErrorToast: true },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] }),
    }),
    mutationPassword: useRequestMutation({
      mutationFnRequestProps: (data: {
        current_password: string;
        new_password: string;
      }) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/me/password",
      }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] }),
    }),
    mutationPhone: useRequestMutation({
      mutationFnRequestProps: (data: { otp: string; phone: string }) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/me/phone",
      }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] }),
    }),
    mutationPut: useRequestMutation({
      mutationFnRequestProps: (data: Partial<Me>) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/me",
      }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [keyMe(), locale] }),
    }),
    mutationResetPassword: useRequestMutation({
      mutationFnRequestProps: (data: Pick<Me, "email">) => ({
        body: JSON.stringify(data),
        requestType: "default",
        urlBackendPath: "/password/forgot",
      }),
    }),
    mutationMergeAccountCheck: useRequestMutation({
      mutationFnRequestProps: (
        data:
          | {
              phone: string;
            }
          | {
              email: string;
            },
      ) => ({
        body: JSON.stringify(data),
        urlBackendPath: "/me/account-merge/check",
      }),
    }),
    mutationMergeAccountMerge: useRequestMutation({
      mutationFnRequestProps: (data: { code: string }) => ({
        body: JSON.stringify(data),
        urlBackendPath: "/me/account-merge/merge",
      }),
    }),
    mutationCompleteResetPassword: useRequestMutation({
      mutationFnRequestProps: (data: { password: string; token: string }) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/password/reset",
        requestType: "other",
      }),
    }),
    query: useRequestQuery({
      queryFnRequestProps: requestMeProps,
      queryKey: keyMe(),
    }),
  };
};
